import { AttributeListState, attributeListStateKey } from './attribute-list.state';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { AttributeListRowModel } from '../models/attribute-list-row.model';
import { AttributeListDataModel } from '../models/attribute-list-data.model';
import { AttributeListColumnModel } from '../models/attribute-list-column.model';
import { AttributeListTabModel } from '../models/attribute-list-tab.model';
import { AttributeListPagingDataType } from '../models/attribute-list-paging-data.type';
import { selectComponentTitle } from '../../../state/core.selectors';
import { BaseComponentTypeEnum, HiddenLayerFunctionality } from '@tailormap-viewer/api';
import { selectVisibleLayersWithAttributes } from '../../../map';
import { AttributeListInitialDataSortModelWithoutSource } from '../models/attribute-list-initial-data-sort.model';

const selectAttributeListState = createFeatureSelector<AttributeListState>(attributeListStateKey);

export const selectAttributeListVisible = createSelector(selectAttributeListState, state => state.visible);
export const selectAttributeListTabs = createSelector(selectAttributeListState, state => state.tabs);
export const selectAttributeListData = createSelector(selectAttributeListState, state => state.data);
export const selectAttributeListSelectedTab = createSelector(selectAttributeListState, state => state.selectedTabId);
export const selectCurrentlyHighlightedFeature = createSelector(selectAttributeListState, state => state.highlightedFeature);
export const selectInitialDataSort = createSelector(selectAttributeListState, state => state.initialDataSort || []);

export const selectTabsForVisibleLayers = createSelector(
  selectVisibleLayersWithAttributes,
  layers => {
    return layers
      .filter(l => !l.hiddenFunctionality?.includes(HiddenLayerFunctionality.attributeList))
      .map(l => ({ id: l.id, label: l.title || l.layerName }));
  },
);

export const selectIsLoadingTabs = createSelector(
  selectTabsForVisibleLayers,
  selectAttributeListTabs,
  (tabsForLayers, createdTabs) => {
    // When we have layers with attributes but no tabs created yet, we are in the process of loading tabs, so we show a loading state.
    // If we have tabs created, we assume loading is done.
    return tabsForLayers.length > 0 && createdTabs.length === 0;
  },
);

export const selectAttributeListTab = (tabId: string) => createSelector(
  selectAttributeListTabs,
  tabs => tabs.find(t => t.id === tabId),
);

export const selectDataWithSort = createSelector(
  selectAttributeListTabs,
  selectAttributeListData,
  selectInitialDataSort,
  (tabs, data, initialDataSort): AttributeListDataModel[] => {
    const tabsById = new Map<string, AttributeListTabModel>(tabs.map(tab => [ tab.id, tab ]));
    const getKey = (tabSourceId: string, layerId: string, source: string, featureType: string | undefined) => {
      return [ tabSourceId, layerId, featureType ?? '', source ].join('___');
    };
    const sortDict = new Map<string, AttributeListInitialDataSortModelWithoutSource>(initialDataSort.map(s => {
      return [ getKey(s.tabSourceId, s.layerId, s.source, s.featureType), s ];
    }));
    return data.map(d => {
      const tab = tabsById.get(d.tabId);
      if (!tab || !tab.layerId) {
        return d;
      }
      const bookmarkKey = getKey(tab.tabSourceId, tab.layerId, 'bookmark', d.featureType);
      const configKey = getKey(tab.tabSourceId, tab.layerId, 'config', d.featureType);
      const sortToApply = sortDict.get(bookmarkKey) || sortDict.get(configKey);
      const hasExplicitSort = typeof d.sortedColumn !== 'undefined';
      if (hasExplicitSort || !sortToApply || (sortToApply.sortDirection === d.sortDirection && sortToApply.sortedColumn === d.sortedColumn)) {
        return d;
      }
      return {
        ...d,
        sortDirection: sortToApply.sortDirection ?? '',
        sortedColumn: sortToApply.sortedColumn,
      };
    });
  },
);

export const selectAttributeListDataSort = createSelector(
  selectAttributeListTabs,
  selectDataWithSort,
  (tabs, data): AttributeListInitialDataSortModelWithoutSource[] => {
    const tabsById = new Map<string, AttributeListTabModel>(tabs.map(tab => [ tab.id, tab ]));
    return data
      .map<AttributeListInitialDataSortModelWithoutSource | null>(dataForTab => {
        const tab = tabsById.get(dataForTab.tabId);
        if (!tab || !dataForTab.sortedColumn || dataForTab.sortDirection === '' || !tab.layerId) {
          return null;
        }
        return {
          tabSourceId: tab.tabSourceId,
          layerId: tab.layerId,
          featureType: dataForTab.featureType,
          sortedColumn: dataForTab.sortedColumn,
          sortDirection: dataForTab.sortDirection,
        };
      })
      .filter((item): item is AttributeListInitialDataSortModelWithoutSource => item !== null);
  });

export const selectAttributeListTabData = (tabId: string) => createSelector(
  selectDataWithSort,
  data => data.filter(t => t.tabId === tabId),
);

export const selectAttributeListDataForId = (dataId: string) => createSelector(
  selectDataWithSort,
  data => data.find(t => t.id === dataId),
);

export const selectAttributeListTabForDataId = (dataId: string) => createSelector(
  selectAttributeListDataForId(dataId),
  selectAttributeListTabs,
  (data, tabs): AttributeListTabModel | null => {
    return data ? (tabs.find(t => t.id === data.tabId) || null) : null;
  },
);

export const selectAttributeListRow = (dataId: string, rowId: string) => createSelector(
  selectAttributeListDataForId(dataId),
  (data): AttributeListRowModel | null => {
    return data ? (data.rows.find(r => r.id === rowId) || null) : null;
  },
);

export const selectSelectedTab = createSelector(
  selectAttributeListSelectedTab,
  selectAttributeListTabs,
  (selectedTab, tabs): AttributeListTabModel | null => {
    return tabs.find(t => t.id === selectedTab) || null;
  },
);

export const selectSelectedTabLayerId = createSelector(
  selectSelectedTab,
  (selectedTab): string | null => {
    if (!selectedTab || typeof selectedTab.layerId === 'undefined') {
      return null;
    }
    return selectedTab.layerId;
  },
);

export const selectDataIdForSelectedTab = createSelector(
  selectSelectedTab,
  (selectedTab): string | null => {
    return selectedTab ? selectedTab.selectedDataId : null;
  },
);

export const selectDataForSelectedTab = createSelector(
  selectSelectedTab,
  selectDataWithSort,
  (selectedTab, data): AttributeListDataModel | null => {
    if (!selectedTab) {
      return null;
    }
    return data.find(d => d.id === selectedTab.selectedDataId) || null;
  },
);

export const selectLoadingDataSelectedTab = createSelector(
  selectSelectedTab,
  (tab): boolean => {
    return tab ? tab.loadingData : false;
  },
);

export const selectLoadErrorForSelectedTab = createSelector(
  selectSelectedTab,
  (tab): string | undefined => tab?.loadingError,
);

export const selectRowsForSelectedTab = createSelector(
  selectDataForSelectedTab,
  (data): AttributeListRowModel[] => {
    return data ? data.rows : [];
  },
);

export const selectColumnsForSelectedTab = createSelector(
  selectDataForSelectedTab,
  (data): AttributeListColumnModel[] => {
    return data ? data.columns.filter(c => c.visible) : [];
  },
);

export const selectColumnsForData = (dataId: string) => createSelector(
  selectDataWithSort,
  (allData): AttributeListColumnModel[] => {
    const data = allData.find(d => d.id === dataId);
    return data ? data.columns : [];
  },
);

export const selectRowCountForSelectedTab = createSelector(
  selectRowsForSelectedTab,
  rows => rows.length,
);

export const selectHasNoRowsForSelectedTab = createSelector(
  selectRowCountForSelectedTab,
  selectLoadErrorForSelectedTab,
  selectLoadingDataSelectedTab,
  (rowCount, error, loadingData) => rowCount === 0 && !error && !loadingData,
);

export const selectSortForSelectedTab = createSelector(
  selectDataForSelectedTab,
  (data): { column: string; direction: string } | null => {
    return data && data.sortedColumn ? { column: data.sortedColumn, direction: data.sortDirection } : null;
  },
);

export const selectPagingDataSelectedTab = createSelector(
  selectDataForSelectedTab,
  (data: AttributeListDataModel | null): AttributeListPagingDataType => {
    if (!data) {
      return { id: '', totalCount: 0, pageIndex: 0, pageSize: 0 };
    }
    return { id: data.id, totalCount: data.totalCount, pageIndex: data.pageIndex, pageSize: data.pageSize };
  },
);

export const selectSelectedRowIdForSelectedTab = createSelector(
  selectDataForSelectedTab,
  (data): string | undefined => {
    return data?.selectedRowId || undefined;
  },
);

export const selectCurrentlySelectedFeatureGeometry = createSelector(
  selectAttributeListVisible,
  selectCurrentlyHighlightedFeature,
  (attributeListVisible, feature): string | null => {
    if (!attributeListVisible) {
      return null;
    }
    return feature?.geometry || null;
  },
);

export const selectAttributeListPanelTitle = selectComponentTitle(
  BaseComponentTypeEnum.ATTRIBUTE_LIST,
  $localize `:@@core.attribute-list.title:Attribute list`,
);

export const selectCheckedRowsForSelectedTab = createSelector(
  selectDataForSelectedTab,
  data => {
    return data ? data.checkedRows.map(r => r.id) : [];
  },
);

export const selectCheckedFeaturesForSelectedTab = createSelector(
  selectDataForSelectedTab,
  data => {
    return data ? data.checkedRows.map(r => r.__fid) : [];
  },
);

export const selectCurrentlyVisibleCheckedFeaturesForSelectedTab = createSelector(
  selectDataForSelectedTab,
  data => {
    const visibleRows = new Set((data?.rows ?? []).map(r => r.id));
    return data ? data.checkedRows
      .filter(r => visibleRows.has(r.id))
      .map(r => r.__fid) : [];
  },
);
